'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, CheckCircle2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { feedingService } from '@/lib/services/farmService';
import { dropdownService } from '@/lib/services/dropdownService';

interface FeedingFields {
  bedId: string;
  feedType: string;
  feedQuantityKg: string;
  beddingMaterial: string;
  moistureLevel: string;
  phReading: string;
  feedingDate: string;
  notes: string;
}

const DEFAULT_BEDS = ['Bed 01','Bed 02','Bed 03','Bed 04','Bed 05','Bed 06','Bed 07','Bed 08'];

export default function FeedingForm({ onSaved }: { onSaved?: () => void }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const [feedTypes, setFeedTypes] = useState<string[]>([]);
  const [beddingMaterials, setBeddingMaterials] = useState<string[]>([]);
  const [moistureLevels, setMoistureLevels] = useState<string[]>([]);

  useEffect(() => {
    Promise.all([
      dropdownService.getByCategory('feed_type'),
      dropdownService.getByCategory('bedding_material'),
      dropdownService.getByCategory('moisture_level'),
    ]).then(([ft, bm, ml]) => {
      setFeedTypes(ft.length > 0 ? ft : ['Kitchen scraps', 'Fruit pulp', 'Vegetable waste', 'Coffee grounds', 'Paper/cardboard', 'Manure', 'Mixed organic', 'Grain bran']);
      setBeddingMaterials(bm.length > 0 ? bm : ['Coco coir', 'Shredded cardboard', 'Straw', 'Leaf litter', 'Peat moss', 'Mixed bedding']);
      setMoistureLevels(ml.length > 0 ? ml : ['Too dry (<50%)', 'Optimal (60-80%)', 'Too wet (>80%)']);
    });
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FeedingFields>({ defaultValues: { feedingDate: new Date().toISOString().split('T')[0] } });

  const onSubmit = async (data: FeedingFields) => {
    if (!user) {
      toast.error('You must be signed in to save records');
      return;
    }
    setLoading(true);
    try {
      const result = await feedingService.create({
        userId: user.id,
        bedId: data.bedId,
        feedType: data.feedType,
        feedQuantityKg: parseFloat(data.feedQuantityKg),
        beddingMaterial: data.beddingMaterial || undefined,
        moistureLevel: data.moistureLevel || undefined,
        phReading: data.phReading ? parseFloat(data.phReading) : undefined,
        feedingDate: data.feedingDate,
        notes: data.notes || undefined,
      });
      if (result) {
        setSaved(true);
        toast.success('Feeding log saved');
        setTimeout(() => setSaved(false), 3000);
        reset({ feedingDate: new Date().toISOString().split('T')[0] });
        onSaved?.();
      } else {
        toast.error('Failed to save feeding log. Please try again.');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save feeding log');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm">
      <div className="px-4 py-3 border-b border-border">
        <h2 className="text-base font-bold text-foreground">Record Feeding</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Logged by: {user?.user_metadata?.full_name || user?.email || 'You'}</p>
      </div>

      {saved && (
        <div className="mx-4 mt-4 flex items-center gap-2 bg-success-bg border border-success/20 rounded-lg px-3 py-2.5">
          <CheckCircle2 size={15} className="text-success" />
          <span className="text-sm font-medium text-success">Feeding log saved!</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
        {/* Required fields */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Bed <span className="text-danger">*</span>
            </label>
            <select
              className={`w-full rounded-xl border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${errors.bedId ? 'border-danger' : 'border-border'}`}
              {...register('bedId', { required: 'Select a bed' })}
            >
              <option value="">Select…</option>
              {DEFAULT_BEDS.map((b) => <option key={`opt-bed-${b}`} value={b}>{b}</option>)}
            </select>
            {errors.bedId && <p className="mt-1 text-xs text-danger">{errors.bedId.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className={`w-full rounded-xl border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${errors.feedingDate ? 'border-danger' : 'border-border'}`}
              {...register('feedingDate', { required: 'Date required' })}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            Feed Type <span className="text-danger">*</span>
          </label>
          <select
            className={`w-full rounded-xl border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${errors.feedType ? 'border-danger' : 'border-border'}`}
            {...register('feedType', { required: 'Feed type required' })}
          >
            <option value="">Select feed type…</option>
            {feedTypes.map((f) => <option key={`opt-feed-${f}`} value={f}>{f}</option>)}
          </select>
          {errors.feedType && <p className="mt-1 text-xs text-danger">{errors.feedType.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            Quantity (kg) <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            step="0.1"
            min="0.1"
            className={`w-full rounded-xl border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${errors.feedQuantityKg ? 'border-danger' : 'border-border'}`}
            placeholder="e.g. 2.5"
            {...register('feedQuantityKg', { required: 'Quantity required', min: { value: 0.1, message: 'Min 0.1 kg' } })}
          />
          {errors.feedQuantityKg && <p className="mt-1 text-xs text-danger">{errors.feedQuantityKg.message}</p>}
        </div>

        {/* Optional fields toggle */}
        <button
          type="button"
          onClick={() => setShowOptional((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-primary font-medium py-1"
        >
          <ChevronDown size={15} className={`transition-transform ${showOptional ? 'rotate-180' : ''}`} />
          {showOptional ? 'Hide' : 'Add'} optional details
        </button>

        {showOptional && (
          <div className="space-y-4 pt-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Bedding</label>
                <select
                  className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  {...register('beddingMaterial')}
                >
                  <option value="">None added</option>
                  {beddingMaterials.map((b) => <option key={`opt-bed-mat-${b}`} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Moisture</label>
                <select
                  className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  {...register('moistureLevel')}
                >
                  <option value="">Not checked</option>
                  {moistureLevels.map((m) => <option key={`opt-moist-${m}`} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">pH Reading</label>
              <input
                type="number"
                step="0.1"
                min="4"
                max="10"
                className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="e.g. 7.0 (optimal: 6.5–7.5)"
                {...register('phReading')}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Notes</label>
              <textarea
                rows={3}
                className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Observations about bed condition or worm behaviour…"
                {...register('notes')}
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 rounded-xl bg-primary text-primary-foreground font-bold text-base hover:opacity-90 transition-all active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
        >
          {loading ? <><Loader2 size={18} className="animate-spin" /> Saving…</> : '✓ Save Feeding Log'}
        </button>
      </form>
    </div>
  );
}